import { createContext, useContext, ReactNode } from "react";
import type { ReportTemplate, ReportFilters } from "@/data/reports/types";
import { useFavoritesBackend } from "@/lib/hooks/use-favorites";

export interface FavoriteItem {
  id: string;
  type: "person" | "company" | "deal" | "query" | "report";
  name: string;
  subtitle: string;
  /** For queries: the original prompt */
  query?: string;
  /** For queries: the AI response */
  response?: string;
  /** For reports: which template */
  reportTemplate?: ReportTemplate;
  /** For reports: saved filter state to re-run with */
  reportFilters?: ReportFilters;
  /** For reports: optional description */
  reportDescription?: string;
  /** For custom reports: ordered list of block ids. */
  customBlocks?: string[];
  /**
   * For v2 reports: pointer to the first-class report_definition. A favorite
   * carrying this is a pointer, not a v1 template blob — it routes to
   * /reports/:id and bypasses the v1 availability hiding. The C3 backfill
   * converges on this key.
   */
  reportDefinitionId?: string;
  /** For companies: website domain for logo lookup */
  domain?: string;
}

interface FavoritesContextType {
  favorites: FavoriteItem[];
  isFavorite: (id: string, type?: FavoriteItem["type"]) => boolean;
  toggleFavorite: (item: FavoriteItem) => void;
  /** Add or replace a favorite (used when re-saving a report with new filters) */
  upsertFavorite: (item: FavoriteItem) => void;
  removeFavorite: (id: string, type: FavoriteItem["type"]) => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const favoritesData = useFavoritesBackend();

  return (
    <FavoritesContext.Provider value={favoritesData}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
